import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/pages/home/context/LangContext";
import DemoModal from "./DemoModal";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/Toast";
import { DEMO_CREDENTIALS, type DemoKind } from "@/lib/demo";

// Illustrative front-desk numbers for the product mock-up — a sample
// hotel's day, not claims about Diyafa's customer base.
const liveStats = [
  { valueEn: "12", valueAr: "12", labelEn: "Arrivals", labelAr: "وصول" },
  { valueEn: "9",  valueAr: "9",  labelEn: "Departures", labelAr: "مغادرة" },
  { valueEn: "44", valueAr: "44", labelEn: "In-house", labelAr: "مقيمون" },
  { valueEn: "0",  valueAr: "0",  labelEn: "Rasad pending", labelAr: "بانتظار رصد" },
];

const floatingCards = [
  {
    icon: "ri-user-received-line",
    color: "text-gold",
    bg: "bg-gold/15",
    titleEn: "Check-In Complete",
    titleAr: "اكتمل تسجيل الوصول",
    subEn: "Khalid Al Balushi · Room 204",
    subAr: "خالد البلوشي · غرفة 204",
    badge: "✓",
    badgeColor: "bg-green-500",
  },
  {
    icon: "ri-shield-check-line",
    color: "text-green-400",
    bg: "bg-green-500/15",
    titleEn: "Rasad Submitted",
    titleAr: "تم الإرسال لرصد",
    subEn: "Auto-submitted · 0.3s",
    subAr: "إرسال تلقائي · 0.3 ثانية",
    badge: "✓",
    badgeColor: "bg-green-500",
  },
  {
    icon: "ri-bar-chart-box-line",
    color: "text-gold",
    bg: "bg-gold/15",
    titleEn: "Occupancy Today",
    titleAr: "الإشغال اليوم",
    subEn: "87% · 44 of 52 rooms",
    subAr: "87% · 44 من 52 غرفة",
    badge: "↑",
    badgeColor: "bg-gold",
  },
];

export default function Hero() {
  const heroRef = useRef<HTMLElement>(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const [activeCard, setActiveCard] = useState(0);
  const { isAr, t } = useLang();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { toast } = useToast();
  const [demoLoading, setDemoLoading] = useState<DemoKind | null>(null);

  // One click from the landing page into a seeded demo account.
  const tryDemo = async (kind: DemoKind) => {
    setDemoLoading(kind);
    const { email, password } = DEMO_CREDENTIALS[kind];
    const { error } = await signIn(email, password);
    setDemoLoading(null);
    if (error) {
      toast.error(t("Demo sign-in failed", "فشل الدخول التجريبي"), t("Please try again in a moment.", "يرجى المحاولة بعد قليل."));
      return;
    }
    navigate("/dashboard");
  };

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        heroRef.current.style.backgroundPositionY = `${window.scrollY * 0.3}px`;
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCard(prev => (prev + 1) % floatingCards.length);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <section
        ref={heroRef}
        className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
        style={{
          backgroundImage: "url('https://readdy.ai/api/search-image?query=luxurious%20Omani%20hotel%20lobby%20interior%20with%20traditional%20Arabic%20architecture%2C%20warm%20golden%20lighting%2C%20ornate%20arches%2C%20marble%20floors%2C%20lush%20plants%2C%20elegant%20chandeliers%2C%20rich%20amber%20and%20brown%20tones%2C%20high-end%20hospitality%20atmosphere%2C%20wide%20angle%20architectural%20photography&width=1920&height=1080&seq=hero-bg-diyafa&orientation=landscape')",
          backgroundSize: "cover",
          backgroundPosition: "center top",
        }}
        dir={isAr ? "rtl" : "ltr"}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/60 to-ink/80"></div>

        {/* Arabesque pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4A843' fill-opacity='1'%3E%3Cpath d='M50 50v-6h-3v6h-6v3h6v6h3v-6h6v-3h-6zm0-40V4h-3v6h-6v3h6v6h3v-6h6V4h-6zM10 50v-6H7v6H1v3h6v6h3v-6h6v-3h-6zM10 10V4H7v6H1v3h6v6h3v-6h6V4h-6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>

        {/* Warm glow accents */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-gold/8 blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-1/3 left-1/4 w-64 h-64 rounded-full bg-[#92400E]/15 blur-3xl pointer-events-none"></div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 py-24 pt-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* LEFT: Copy */}
            <div className={isAr ? "text-right" : "text-left"}>
              {/* Badge */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 ${isAr ? "flex-row-reverse" : ""}`}
                style={{ background: "rgba(212,168,67,0.12)", borderColor: "rgba(212,168,67,0.35)" }}>
                <div className="w-2 h-2 rounded-full bg-gold animate-pulse flex-shrink-0"></div>
                <span className={`text-[13px] font-semibold text-gold-light ${isAr ? "font-tajawal" : ""}`}>
                  {t("Rasad-ready hotel & car rental platform for Oman", "منصة فنادق وتأجير سيارات جاهزة لرصد في عُمان")}
                </span>
              </div>

              {/* Headline */}
              <h1 className={`font-bold leading-tight mb-5 text-white ${isAr ? "font-tajawal text-4xl md:text-5xl" : "font-inter text-4xl md:text-5xl lg:text-6xl"}`}>
                {isAr ? (
                  <>
                    <span className="text-gold-light">أتقن</span>
                    <br />
                    فن الضيافة
                  </>
                ) : (
                  <>
                    Master the
                    <br />
                    <span className="text-gold-light">Art of Hospitality</span>
                  </>
                )}
              </h1>

              {/* Tagline */}
              <p className={`text-base md:text-lg leading-relaxed mb-8 max-w-lg text-white/75 ${isAr ? "font-tajawal" : ""}`}>
                {t(
                  "The all-in-one platform for hotels and car rental businesses in Oman — from guest check-in to automated Rasad compliance, everything in one place.",
                  "المنصة الشاملة للفنادق وشركات تأجير السيارات في عُمان — من تسجيل وصول الضيوف إلى الامتثال التلقائي لرصد، كل شيء في مكان واحد."
                )}
              </p>

              {/* CTAs */}
              <div className={`flex flex-wrap gap-3 mb-10 ${isAr ? "justify-end" : "justify-start"}`}>
                <button
                  onClick={() => navigate("/register")}
                  className={`flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-ink bg-gold transition-all cursor-pointer hover:bg-gold-dark whitespace-nowrap text-sm ${isAr ? "font-tajawal flex-row-reverse" : ""}`}
                >
                  <i className="ri-rocket-line"></i>
                  {t("Start Free Trial", "ابدأ مجاناً")}
                </button>
                <button
                  onClick={() => setDemoOpen(true)}
                  className={`flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-white border border-white/25 transition-all cursor-pointer hover:bg-white/10 whitespace-nowrap text-sm ${isAr ? "font-tajawal flex-row-reverse" : ""}`}
                  style={{ background: "rgba(255,255,255,0.07)" }}
                >
                  <i className="ri-play-circle-line text-gold-light"></i>
                  {t("Watch Demo", "شاهد العرض")}
                </button>
              </div>

              {/* One-click demo */}
              <div className={`flex flex-wrap items-center gap-2 -mt-6 mb-10 ${isAr ? "justify-end" : "justify-start"}`}>
                <span className={`text-sm text-white/65 ${isAr ? "font-tajawal" : ""}`}>
                  {t("Try the live demo — no sign-up:", "جرّب النسخة التجريبية — بدون تسجيل:")}
                </span>
                {([
                  { kind: "hotel",  icon: "ri-building-line", en: "Hotel",      ar: "فندق" },
                  { kind: "rental", icon: "ri-car-line",      en: "Car rental", ar: "تأجير سيارات" },
                ] as const).map((d) => (
                  <button
                    key={d.kind}
                    onClick={() => tryDemo(d.kind)}
                    disabled={demoLoading !== null}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-semibold text-gold-light border border-gold/40 hover:bg-gold/10 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-wait ${isAr ? "font-tajawal" : ""}`}
                  >
                    <i className={demoLoading === d.kind ? "ri-loader-4-line animate-spin" : d.icon} aria-hidden="true"></i>
                    {isAr ? d.ar : d.en}
                  </button>
                ))}
              </div>

              {/* Trust badges */}
              <div className={`flex flex-wrap gap-5 ${isAr ? "justify-end" : "justify-start"}`}>
                {[
                  { icon: "ri-shield-check-line", en: "Rasad Compliant", ar: "متوافق مع رصد" },
                  { icon: "ri-lock-line", en: "Secure & Encrypted", ar: "آمن ومشفر" },
                  { icon: "ri-customer-service-2-line", en: "24/7 Support", ar: "دعم 24/7" },
                ].map((b, i) => (
                  <div key={i} className={`flex items-center gap-2 ${isAr ? "flex-row-reverse" : ""}`}>
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${b.icon} text-gold-light text-sm`}></i>
                    </div>
                    <span className={`text-sm font-medium text-white/65 ${isAr ? "font-tajawal" : ""}`}>
                      {isAr ? b.ar : b.en}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT: Live activity cards */}
            <div className="relative flex flex-col gap-4">
              {/* Main dashboard preview card */}
              <div
                className="rounded-2xl p-5 border border-white/10"
                style={{ background: "rgba(26,26,46,0.75)", backdropFilter: "blur(16px)" }}
              >
                <div className={`flex items-center justify-between mb-4 ${isAr ? "flex-row-reverse" : ""}`}>
                  <div className={`flex items-center gap-2 ${isAr ? "flex-row-reverse" : ""}`}>
                    <div className="w-8 h-8 rounded-lg bg-gold flex items-center justify-center">
                      <span className="text-white font-bold text-sm font-tajawal">ض</span>
                    </div>
                    <span className="text-white font-semibold text-sm">Diyafa Dashboard</span>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-500/15 border border-green-500/30">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
                    <span className={`text-[11px] font-semibold text-green-400 ${isAr ? "font-tajawal" : ""}`}>
                      {t("Live", "مباشر")}
                    </span>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {liveStats.map((s, i) => (
                    <div key={i} className="rounded-xl p-3 text-center" style={{ background: "rgba(255,255,255,0.05)" }}>
                      <p className="font-bold text-gold-light text-lg leading-none mb-1">{isAr ? s.valueAr : s.valueEn}</p>
                      <p className={`text-[10px] text-white/50 leading-tight ${isAr ? "font-tajawal" : ""}`}>{isAr ? s.labelAr : s.labelEn}</p>
                    </div>
                  ))}
                </div>

                {/* Occupancy bar */}
                <div className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.05)" }}>
                  <div className={`flex items-center justify-between mb-2 ${isAr ? "flex-row-reverse" : ""}`}>
                    <span className={`text-xs text-white/60 ${isAr ? "font-tajawal" : ""}`}>{t("Today's Occupancy", "إشغال اليوم")}</span>
                    <span className="text-xs font-bold text-gold-light">87%</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-gold to-gold-light" style={{ width: "87%" }}></div>
                  </div>
                  <div className={`flex justify-between mt-1.5 ${isAr ? "flex-row-reverse" : ""}`}>
                    <span className="text-[10px] text-white/40">{t("44 occupied", "44 مشغولة")}</span>
                    <span className="text-[10px] text-white/40">{t("8 available", "8 متاحة")}</span>
                  </div>
                </div>
              </div>

              {/* Floating activity cards */}
              <div className="grid grid-cols-1 gap-3">
                {floatingCards.map((card, i) => (
                  <div
                    key={i}
                    className={`rounded-xl p-4 border transition-all duration-500 ${isAr ? "flex-row-reverse" : ""} ${
                      activeCard === i
                        ? "border-gold/40 scale-[1.01]"
                        : "border-white/8 opacity-70"
                    }`}
                    style={{
                      background: activeCard === i
                        ? "rgba(212,168,67,0.08)"
                        : "rgba(26,26,46,0.6)",
                      backdropFilter: "blur(12px)",
                    }}
                  >
                    <div className={`flex items-center gap-3 ${isAr ? "flex-row-reverse" : ""}`}>
                      <div className={`w-9 h-9 rounded-xl ${card.bg} flex items-center justify-center flex-shrink-0`}>
                        <i className={`${card.icon} ${card.color} text-base`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold text-white ${isAr ? "font-tajawal" : ""}`}>
                          {isAr ? card.titleAr : card.titleEn}
                        </p>
                        <p className={`text-[11px] text-white/50 ${isAr ? "font-tajawal" : ""}`}>
                          {isAr ? card.subAr : card.subEn}
                        </p>
                      </div>
                      <div className={`w-6 h-6 rounded-full ${card.badgeColor} flex items-center justify-center flex-shrink-0`}>
                        <span className="text-white text-[11px] font-bold">{card.badge}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Rasad compliance badge */}
              <div
                className="rounded-xl p-4 border border-green-500/25"
                style={{ background: "rgba(34,197,94,0.08)", backdropFilter: "blur(12px)" }}
              >
                <div className={`flex items-center gap-3 ${isAr ? "flex-row-reverse" : ""}`}>
                  <div className="w-9 h-9 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-green-400 text-base"></i>
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-bold text-green-300 ${isAr ? "font-tajawal" : ""}`}>
                      {t("Rasad Compliance: 100%", "امتثال رصد: 100%")}
                    </p>
                    <p className={`text-[11px] text-green-400/60 ${isAr ? "font-tajawal" : ""}`}>
                      {t("All submissions up to date", "جميع الإرسالات محدّثة")}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map(j => (
                      <div key={j} className="w-1.5 h-4 rounded-full bg-green-400/60"></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-cream to-transparent pointer-events-none"></div>
      </section>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </>
  );
}
